// UserKpiManagement.jsx - Main Component for User KPI Management

import ContentContainer from '@libComponents/ContentContainer';
import FilterInput from '@libComponents/FilterInput';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    Typography
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import EditUserKpiDialog from './EditUserKpiDialog';
import FilterPanel from './FilterPanel';
import ManageGoalsDialog from './ManageGoalsDialog';
import ScoreManagementDialog from './ScoreManagementDialog';
import UserKpiDialog from './UserKpiDialog';
import UserKpiTable from './UserKpiTable';

const UserKpiManagement = () => {
    const dispatch = useDispatch();
    const userKpis = useSelector(selectors.selectUserKpis);
    const loading = useSelector(selectors.selectLoading);
    const pagination = useSelector(selectors.selectPagination);
    const filters = useSelector(selectors.selectFilters);
    const sort = useSelector(selectors.selectSort);
    const navigate = useNavigate();
    const location = useLocation();
    // Local state for search input (immediate UI feedback)
    const [searchInput, setSearchInput] = useState('');
    const debounceTimerRef = useRef(null);

    // Initialize from URL parameters on mount
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const searchParam = params.get('search') || '';
        const offsetParam = params.get('page') ? parseInt(params.get('page'), 10) : 1;
        const limitParam = params.get('limit') ? parseInt(params.get('limit'), 10) : pagination.limit;
        const sortByParam = params.get('sortBy') || sort.sortBy;
        const sortDescendingParam = params.has('sortDescending') 
            ? params.get('sortDescending') === 'true' 
            : sort.sortDescending;
        if (searchParam !== filters.search) {
            dispatch(actions.setUserKpiFilters({ search: searchParam }));
        }
        if (offsetParam !== pagination.offset || limitParam !== pagination.limit) {
            dispatch(actions.setUserKpiPagination({ offset: offsetParam, limit: limitParam }));
        }
        if (sortByParam !== sort.sortBy || sortDescendingParam !== sort.sortDescending) {
            dispatch(actions.setUserKpiFilters({ sortBy: sortByParam, sortDescending: sortDescendingParam }));
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync local search input with Redux filter
    useEffect(() => {
        setSearchInput(filters.search);
    }, [filters.search]);

    // Sync URL with current state
    useEffect(() => {
        const params = new URLSearchParams();
        if (filters.search) params.set('search', filters.search);
        if (pagination.offset && pagination.offset !== 1) params.set('page', pagination.offset.toString());
        if (pagination.limit && pagination.limit !== 10) params.set('limit', pagination.limit.toString());
        if (sort.sortBy) params.set('sortBy', sort.sortBy);
        if (sort.sortDescending) params.set('sortDescending', 'true');
        const newSearch = params.toString();
        if (location.search !== `?${newSearch}` && newSearch !== location.search.substring(1)) {
            navigate(`?${newSearch}`, { replace: true });
        }
    }, [filters.search, pagination.offset, pagination.limit, sort.sortBy, sort.sortDescending, navigate, location.search]);

    // Fetch dropdown data once on mount
    useEffect(() => {
        dispatch(actions.fetchAllUsersRequest());
        dispatch(actions.fetchAllPeriodsRequest());
    }, [dispatch]);

    // Fetch user KPIs whenever filters, pagination, or sort changes
    useEffect(() => {
        dispatch(actions.fetchUserKpisRequest());
    }, [dispatch, pagination.limit, pagination.offset, filters.search, filters.userId, filters.periodId, filters.departmentId, filters.reportingManager, filters.directLineManager, filters.sortBy, filters.sortDescending]);

    const handleCreateUserKpi = () => {
        dispatch(actions.openUserKpiDialog());
    };

    const handlePageChange = (newOffset) => {
        dispatch(actions.setUserKpiPagination({ offset: newOffset }));
    };

    const handlePageSizeChange = (newLimit) => {
        dispatch(actions.setUserKpiPagination({ limit: newLimit, offset: 1 }));
    };

    // Debounced search handler for FilterInput
    const handleSearch = (value) => {
        setSearchInput(value);
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            if (value === '' || value.length >= 2) {
                dispatch(actions.setUserKpiFilters({ search: value }));
                dispatch(actions.setUserKpiPagination({ offset: 1 }));
            }
        }, 500);
    };

    const handleSearchInputChange = (e) => {
        const value = e.target.value;
        setSearchInput(value);
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            if (value === '' || value.length >= 2) {
                dispatch(actions.setUserKpiFilters({ search: value }));
                dispatch(actions.setUserKpiPagination({ offset: 1 }));
            }
        }, 500);
    };

    const handleFilterChange = (filterName, value) => {
        dispatch(actions.setUserKpiFilters({ [filterName]: value }));
        dispatch(actions.setUserKpiPagination({ offset: 1 }));
    };

    const handleResetFilters = () => {
        setSearchInput('');
        dispatch(actions.resetUserKpiFilters());
    };

    const handleSort = (columnId) => {
        const isSameColumn = sort.sortBy === columnId;
        dispatch(actions.setUserKpiFilters({
            sortBy: columnId,
            sortDescending: isSameColumn ? !sort.sortDescending : false
        }));
    };

    return (
        <ContentContainer
            title=""
            AdornmentComponent={
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {/* Top Row: Title, Search, and Assign KPI Button */}
                    <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Typography variant="h5" sx={{
                            fontWeight: 600,
                            color: (theme) => theme.palette.text.primary,
                            textAlign: { xs: 'center', sm: 'left' }
                        }}>
                            Employee KPIs
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}>
                            <Box sx={{ width: { xs: '100%', sm: 300 } }}>
                                <FilterInput
                                    name="kpi-search"
                                    handleSearch={handleSearch}
                                    value={searchInput}
                                />
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleCreateUserKpi}
                                size="small"
                                sx={{ textTransform: 'none', whiteSpace: 'nowrap', width: { xs: '100%', sm: 'auto' } }}
                            >
                                Assign KPI
                            </Button>
                        </Box>
                    </Box>

                    {/* Bottom Row: Filter Dropdowns */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: { xs: 'center', sm: 'end' },
                        gap: 1,
                        flexWrap: 'wrap'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'end' }, width: '100%' }}>
                            <Typography variant="p" sx={{ fontSize: '14px', fontWeight: 500, color: (theme) => theme.palette.text.primary, mr: 1 }}>
                                Filters:
                            </Typography>
                            <FilterPanel
                                search={searchInput}
                                userId={filters.userId}
                                periodId={filters.periodId}
                                departmentId={filters.departmentId}
                                reportingManager={filters.reportingManager}
                                directLineManager={filters.directLineManager}
                                onSearch={handleSearchInputChange}
                                onFilterChange={handleFilterChange}
                                onReset={handleResetFilters}
                                totalCount={pagination.total}
                            />
                            <Button
                                variant="outlined"
                                onClick={handleResetFilters}
                                startIcon={<RefreshIcon />}
                                size="small"
                                sx={{ height: '34px', minHeight: '34px' }}
                            >
                                Reset
                            </Button>
                        </Box>
                    </Box>
                </Box>
            }
        >
            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Empty State */}
            {!loading && userKpis.length === 0 && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        No user KPI assignments found. Assign your first KPI to get started.
                    </Typography>
                </Box>
            )}

            {/* Table */}
            {!loading && userKpis.length > 0 && (
                <UserKpiTable
                    userKpis={userKpis}
                    pagination={pagination}
                    sort={sort}
                    onSort={handleSort}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            )}

            {/* Dialogs */}
            <UserKpiDialog />
            <ManageGoalsDialog />
            <EditUserKpiDialog />
            <ScoreManagementDialog />
            <DeleteConfirmationDialog />
        </ContentContainer>
    );
};

export default UserKpiManagement;